import { z } from 'zod';
import type { ApiSchemaClass, ApiSchemaDefinition } from './types';
import {
  agentChatBatchResponseSchema,
  agentChatResponseSchema,
  agentCompareResponseSchema,
  agentResourceResponseSchema,
  emptyObjectResponseSchema,
} from './common-responses';
import { chatTriggerSourceSchema } from './chat';
import { editorStateSchema, llmModelIdSchema } from './editor-state';

const agentChatModeSchema = z.enum(['auto', 'manual']);

export const agentInvokeFileMediaTypeSchema = z.enum([
  'application/pdf',
  'image/png',
  'image/jpeg',
  'image/gif',
  'image/webp',
]);

export const agentInvokeFileSchema = z.object({
  url: z
    .string()
    .describe(
      'Public HTTPS URL of the file (e.g. a signed S3/GCS URL), or a data: URL with base64 content to upload the file inline.'
    ),
  mediaType: agentInvokeFileMediaTypeSchema
    .optional()
    .describe(
      "File type. When omitted, it is taken from the data: URL or the download's Content-Type header."
    ),
  filename: z
    .string()
    .optional()
    .describe('Optional file name shown to the agent.'),
});

export const agentInvokeBodyV20260601Schema = z.object({
  prompt: z
    .string()
    .describe(
      'User prompt to send to the agent. The request blocks until the agent finishes and returns the assistant reply text.'
    ),
  files: z
    .array(agentInvokeFileSchema)
    .max(10)
    .optional()
    .describe(
      'Files attached to the prompt: PDF, PNG, JPEG, GIF or WebP. Images up to 5MB, PDFs up to 25MB, 30MB in total. The agent model must support the file type.'
    ),
  systemSuffix: z
    .string()
    .optional()
    .describe(
      'Additional system prompt text appended for this invocation only (not persisted on the agent).'
    ),
  model: llmModelIdSchema
    .optional()
    .describe(
      "Overrides the agent's configured model for this invocation only."
    ),
  triggerSource: chatTriggerSourceSchema
    .optional()
    .describe(
      'Trigger source recorded on the run. Defaults to "warehouse" when omitted.'
    ),
});

export type AgentInvokeBody = z.infer<typeof agentInvokeBodyV20260601Schema>;

export type AgentInvokeFile = z.infer<typeof agentInvokeFileSchema>;

export const agentChatBodyV20260601Schema = z.object({
  message: z
    .string()
    .describe('User message to send to the agent for this turn.'),
  conversationId: z
    .string()
    .uuid()
    .optional()
    .describe(
      'Existing conversation UUID. Omit to start a new conversation with the agent.'
    ),
  mode: agentChatModeSchema
    .optional()
    .describe(
      'Tool execution mode: "auto" (default) runs tools without approval; "manual" requires user approval before each tool call.'
    ),
  systemSuffix: z
    .string()
    .optional()
    .describe(
      'Additional system prompt text appended for this message only (not persisted on the agent).'
    ),
});

export type AgentChatBody = z.infer<typeof agentChatBodyV20260601Schema>;

export const agentChatBatchBodyV20260601Schema = z
  .array(agentChatBodyV20260601Schema)
  .min(1)
  .max(50)
  .describe('Batch of up to 50 chat requests against the same agent.');

export type AgentChatBatchBody = z.infer<
  typeof agentChatBatchBodyV20260601Schema
>;

export const agentCompareBodyV20260601Schema = z.object({
  message: z
    .string()
    .describe('User message to send to the agent for each model.'),
  models: z
    .array(llmModelIdSchema)
    .min(1)
    .max(10)
    .describe(
      'Models to compare. Each starts its own new conversation with the agent, overriding the agent default model.'
    ),
  mode: agentChatModeSchema
    .optional()
    .describe(
      'Tool execution mode applied to every comparison conversation: "auto" (default) runs tools without approval; "manual" requires user approval before each tool call.'
    ),
  systemSuffix: z
    .string()
    .optional()
    .describe(
      'Additional system prompt text appended for these messages only (not persisted on the agent).'
    ),
});

export type AgentCompareBody = z.infer<typeof agentCompareBodyV20260601Schema>;

export const agentUpdateBodyV20260601Schema = z.object({
  editorState: editorStateSchema
    .optional()
    .describe(
      'Updated TipTap document for the agent prompt. Omit to keep the current editor state.'
    ),
  model: llmModelIdSchema
    .optional()
    .describe('Updated LLM model. Omit to keep the current model.'),
});

export type AgentUpdateBody = z.infer<typeof agentUpdateBodyV20260601Schema>;

export const agentCreateBodyV20260601Schema = z.object({
  editorState: editorStateSchema,
  model: llmModelIdSchema,
  folderId: z
    .string()
    .uuid()
    .optional()
    .describe(
      'Optional folder UUID to place the new agent in. When omitted, the agent is created in the caller organization root.'
    ),
  emoji: z
    .string()
    .nullable()
    .optional()
    .describe(
      'Optional emoji shown on the agent resource card (e.g. "🤖"). Pass null to clear a previously set emoji.'
    ),
});

export type AgentCreateBody = z.infer<typeof agentCreateBodyV20260601Schema>;

export const agentInvokeResponseSchema = z.object({
  success: z
    .boolean()
    .describe('Whether the agent invocation completed successfully.'),
  result: z
    .string()
    .describe(
      'Assistant reply text from the completed agent run (plain text or JSON string for structured outputs).'
    ),
});

export class AgentInvokeV20260601Schema implements ApiSchemaDefinition {
  static readonly version = 'agent-invoke-2026-06-01' as const;

  readonly name = AgentInvokeV20260601Schema.version;
  readonly schema = agentInvokeBodyV20260601Schema;
  readonly response = agentInvokeResponseSchema;
}

export class AgentChatV20260601Schema implements ApiSchemaDefinition {
  static readonly version = 'agent-chat-2026-06-01' as const;

  readonly name = AgentChatV20260601Schema.version;
  readonly schema = agentChatBodyV20260601Schema;
  readonly response = agentChatResponseSchema;
}

export class AgentChatBatchV20260601Schema implements ApiSchemaDefinition {
  static readonly version = 'agent-chat-batch-2026-06-01' as const;

  readonly name = AgentChatBatchV20260601Schema.version;
  readonly schema = agentChatBatchBodyV20260601Schema;
  readonly response = agentChatBatchResponseSchema;
}

export class AgentCompareV20260601Schema implements ApiSchemaDefinition {
  static readonly version = 'agent-compare-2026-06-01' as const;

  readonly name = AgentCompareV20260601Schema.version;
  readonly schema = agentCompareBodyV20260601Schema;
  readonly response = agentCompareResponseSchema;
}

export class AgentUpdateV20260601Schema implements ApiSchemaDefinition {
  static readonly version = 'agent-update-2026-06-01' as const;

  readonly name = AgentUpdateV20260601Schema.version;
  readonly schema = agentUpdateBodyV20260601Schema;
  readonly response = emptyObjectResponseSchema;
}

export class AgentCreateV20260601Schema implements ApiSchemaDefinition {
  static readonly version = 'agent-create-2026-06-01' as const;

  readonly name = AgentCreateV20260601Schema.version;
  readonly schema = agentCreateBodyV20260601Schema;
  readonly response = agentResourceResponseSchema;
}

export const AGENT_API_SCHEMAS = [
  AgentChatV20260601Schema,
  AgentChatBatchV20260601Schema,
  AgentCompareV20260601Schema,
  AgentInvokeV20260601Schema,
  AgentUpdateV20260601Schema,
  AgentCreateV20260601Schema,
] as const satisfies readonly ApiSchemaClass[];
